import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Trash2, Play, Briefcase, ChevronUp, Check, Edit2, ExternalLink, Link, Search, Loader2, Square, FileStack, PenTool, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Select } from '../ui/Select';
import { projectService, webhookService, apiCancelTokens, extractLinkInfo, type LinkedUrl } from '../../services/api';
import { ArticleViewer } from './ArticleViewer';
import type { ArticleTypeId } from '../../types';
import { ARTICLE_TYPES } from '../../types';
import type { ComparisonItem } from './ArticleTypeSelector';
import { useDataCache } from '../../context/DataCacheContext';

interface SchedulerRow {
    id: string;
    projectId: string;
    topic: string; // ID of existing topic or new raw string
    isNewTopic: boolean;
    keyword: string;
    audience: string;
    stylePosts: string[]; // URLs or IDs of WP posts
    externalUrls?: string[]; // URLs externas para análisis de estilo
    includeStructure?: boolean; // true = estilo + estructura, false = solo estilo
    linkedPages?: string[]; // IDs of WP pages
    linkedTaxonomyTerms?: { id: string; taxonomySlug: string; restBase?: string }[]; // Términos de taxonomía seleccionados con su tipo
    linkedUrls?: LinkedUrl[]; // URLs externas scrapeadas para enlaces internos
    status: 'pending' | 'briefing' | 'researching' | 'generating' | 'completed' | 'error';
    progress: number;
    resultArticle?: any;
    errorMsg?: string;
    generatedBrief?: any;
    researchResults?: any[];
    styleAnalysis?: string; // New field for style analysis result
    predefinedStyle?: string; // New field for predefined style
    includeIndex: string; // 'si' | 'no'
    faqDesplegable: string; // 'si' | 'no'
    isExpanded: boolean; // Controls whether the row is fully expanded or collapsed
    // Nuevos campos para tipo de artículo
    articleType: ArticleTypeId;
    comparisonUrls?: string[];
    comparisonItems?: ComparisonItem[];
    comparisonSourceTab?: 'wp-pages' | 'wp-posts' | 'manual';
    mainSourceUrl?: string;
    wordCount: number; // Cantidad de palabras objetivo para el artículo
    // CTA fields
    ctaText?: string;
    ctaUrl?: string;
    // Title mode
    isTitleStrict?: boolean;
}

const PREDEFINED_STYLES = [
    { value: 'wordpress', label: '🎨 Imitar estilo de mis posts (WordPress)' },
    { value: 'external', label: '🌐 URL Externa (Extraer estilo de web)' },
    { value: 'Neutro', label: 'Neutro (Informativo)' },
    { value: 'Profesional', label: 'Profesional (Corporativo)' },
    { value: 'Cercano', label: 'Cercano (Blog Personal)' },
    { value: 'Periodístico', label: 'Periodístico (Noticia)' },
    { value: 'Persuasivo', label: 'Persuasivo (Ventas)' },
    { value: 'Académico', label: 'Académico (Formal)' },
    { value: 'Divertido', label: 'Divertido / Humorístico' }
];

interface WordPressPost {
    id: number;
    title: string;
    link: string;
    content: string; // Needed for style analysis
    excerpt?: string;
}

interface WordPressPage {
    id: number;
    title: string;
    link: string;
    slug: string;
    excerpt?: string;
}

interface ContentSchedulerProps {
    initialProjectId?: string;
    initialArticleType?: ArticleTypeId;
}

export const ContentScheduler: React.FC<ContentSchedulerProps> = ({ initialProjectId, initialArticleType }) => {
    // Usar proyectos del DataCacheContext (sincronizado con actualizaciones)
    const { projects, loadAllData } = useDataCache();

    // const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null); // Removed debounce ref
    const [rows, setRows] = useState<SchedulerRow[]>([]);
    const [hasInitialized, setHasInitialized] = useState(false);
    const [isInitializing, setIsInitializing] = useState(!!initialProjectId);

    // Cache for project data to avoid refetching on every row change
    const [viewingArticle, setViewingArticle] = useState<any | null>(null);
    const [viewingProjectId, setViewingProjectId] = useState<string | undefined>(undefined);
    const [projectTopics, setProjectTopics] = useState<Record<string, any[]>>({});
    const [projectWPPosts, setProjectWPPosts] = useState<Record<string, WordPressPost[]>>({});
    const [projectWPPages, setProjectWPPages] = useState<Record<string, WordPressPage[]>>({});
    const [projectTaxonomies, setProjectTaxonomies] = useState<Record<string, Record<string, any[]>>>({});
    const [taxonomyPagination, setTaxonomyPagination] = useState<Record<string, Record<string, { page: number, hasMore: boolean, total: number }>>>({});
    const [enabledTaxonomies, setEnabledTaxonomies] = useState<Record<string, any[]>>({});
    const [activeDropdown, setActiveDropdown] = useState<{ id: string, top: number, left: number, width: number } | null>(null);
    const [pagesSearch, setPagesSearch] = useState('');
    const [taxonomySearch, setTaxonomySearch] = useState<Record<string, string>>({});
    const [isLoadingPages, setIsLoadingPages] = useState(false);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    // Estado para tab de enlaces (WP pages vs URLs externas vs taxonomías)
    const [linksTab, setLinksTab] = useState<string>('page');
    const [urlInput, setUrlInput] = useState('');
    const [isExtractingUrl, setIsExtractingUrl] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isCancelled, setIsCancelled] = useState(false);
    const cancelledRef = useRef(false);

    // State for general add row dropdown
    const [addMenuOpen, setAddMenuOpen] = useState(false);

    // Simple Toast State
    const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' } | null>(null);

    // Auto-dismiss toast
    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    // Debug: Log enabledTaxonomies changes
    useEffect(() => {
        console.log('[ContentScheduler STATE] enabledTaxonomies updated:', enabledTaxonomies);
    }, [enabledTaxonomies]);

    const toggleAddMenu = (e: React.MouseEvent) => {
        e.stopPropagation();
        setAddMenuOpen(!addMenuOpen);
    };

    // Load Projects from DataCacheContext AND Auto-populate if needed
    useEffect(() => {
        const initializeScheduler = async () => {
            try {
                // Cargar proyectos del cache si no están disponibles
                if (projects.length === 0) {
                    await loadAllData();
                }

                // Auto-add row if initialProjectId is present and we haven't done it yet
                if (initialProjectId && !hasInitialized && projects.length > 0) {
                    setIsInitializing(true); // Ensure loading state is on

                    const newRow: SchedulerRow = {
                        id: `row-${Date.now()}`,
                        projectId: initialProjectId,
                        topic: '',
                        isNewTopic: false,
                        keyword: '',
                        audience: '',
                        stylePosts: [],
                        externalUrls: [],
                        includeStructure: false,
                        linkedPages: [],
                        linkedTaxonomyTerms: [],
                        linkedUrls: [],
                        predefinedStyle: '',
                        includeIndex: '',
                        faqDesplegable: '',
                        status: 'pending',
                        progress: 0,
                        isExpanded: true,
                        articleType: initialArticleType || 'wikipedia',
                        comparisonUrls: [],
                        comparisonItems: [],
                        comparisonSourceTab: 'wp-pages',
                        mainSourceUrl: '',
                        wordCount: 2000,
                        ctaText: '',
                        ctaUrl: '',
                        isTitleStrict: true
                    };

                    setRows(prev => [...prev, newRow]);
                    // Also trigger data fetch for this project AND WAIT for it (but skip pages for speed)
                    await fetchProjectData(initialProjectId, undefined, { skipPages: true });

                    setHasInitialized(true);
                    setIsInitializing(false); // Turn off loading when data is ready

                    // Trigger pages fetch in background now that UI is shown
                    fetchProjectData(initialProjectId);
                }

            } catch (error) {
                setIsInitializing(false);
            }
        };
        initializeScheduler();
    }, [initialProjectId, hasInitialized, projects.length, loadAllData]); // Dependency on projects.length to react when loaded

    // Global listener to close active dropdown on click outside
    useEffect(() => {
        // Scroll listener removed to prevent dropdown from closing on scroll
        // allowing user to see more posts or scroll the list itself if needed.
        // Note: With fixed positioning, the dropdown might float if page scrolls, but this is preferred over disappearing.
    }, [activeDropdown]);

    useEffect(() => {
        // Scroll listener removed to prevent dropdown from closing on scroll
        // allowing user to see more posts or scroll the list itself if needed.
        // Note: With fixed positioning, the dropdown might float if page scrolls, but this is preferred over disappearing.
    }, [activeDropdown]);

    const fetchProjectData = async (projectId: string, search?: string, options: { skipPages?: boolean } = {}) => {
        // Fetch Topics if not cached
        if (!projectTopics[projectId] && !search) {
            try {
                const topicsData = await webhookService.getTopics(projectId);
                let topics: { id: string; url: string }[] = [];
                if (Array.isArray(topicsData)) {
                    topics = topicsData.map((t: any) => ({
                        id: t.id.toString(),
                        url: t.titulo
                    }));
                }
                setProjectTopics(prev => ({ ...prev, [projectId]: topics }));
            } catch (error) {
                // Error fetching topics
            }
        }

        // Always try to fetch WP Posts if not cached (backend returns [] if no credentials)
        if (!projectWPPosts[projectId] && !search) {
            try {
                const posts = await projectService.getWordPressPosts(projectId);
                setProjectWPPosts(prev => ({ ...prev, [projectId]: posts || [] }));
            } catch (e) {
                setProjectWPPosts(prev => ({ ...prev, [projectId]: [] }));
            }
        }

        // Always try to fetch WP Pages if not cached or searching
        if (!options.skipPages && (!projectWPPages[projectId] || search !== undefined)) {
            setIsLoadingPages(true);
            try {
                const pages = await projectService.getWordPressPages(projectId, search);
                setProjectWPPages(prev => ({ ...prev, [projectId]: pages || [] }));
            } catch (e) {
                if (!search) setProjectWPPages(prev => ({ ...prev, [projectId]: [] }));
            } finally {
                setIsLoadingPages(false);
            }
        }

        // Load enabled taxonomies configuration if not cached
        if (!enabledTaxonomies[projectId]) {
            try {
                console.log('[ContentScheduler] Looking for project:', projectId);
                const project = projects.find(p => p.id === projectId);
                console.log('[ContentScheduler] Found project:', project ? 'YES' : 'NO');
                if (project) {
                    console.log('[ContentScheduler] Project taxonomies_config:', (project as any).taxonomies_config);
                }
                if (project && (project as any).taxonomies_config) {
                    const enabled = ((project as any).taxonomies_config || []).filter((t: any) => t.enabled);
                    console.log('[ContentScheduler] Enabled taxonomies:', enabled);
                    setEnabledTaxonomies(prev => ({ ...prev, [projectId]: enabled }));
                }
            } catch (e) {
                console.error('[ContentScheduler] Error loading taxonomies config:', e);
            }
        }
    };

    const fetchTaxonomyTerms = async (projectId: string, taxonomySlug: string, search?: string, page: number = 1, append: boolean = false) => {
        if (!projectTaxonomies[projectId]) {
            setProjectTaxonomies(prev => ({ ...prev, [projectId]: {} }));
            setTaxonomyPagination(prev => ({ ...prev, [projectId]: {} }));
        }

        console.log('[fetchTaxonomyTerms] Llamada:', { projectId, taxonomySlug, search, page, append });
        setIsLoadingPages(true);
        try {
            const response = await projectService.getWordPressTaxonomyTerms(projectId, taxonomySlug, search, page);
            console.log('[fetchTaxonomyTerms] Respuesta:', { items: response.items?.length, total: response.total, hasMore: response.hasMore });
            const newTerms = response.items || [];

            setProjectTaxonomies(prev => ({
                ...prev,
                [projectId]: {
                    ...(prev[projectId] || {}),
                    [taxonomySlug]: append
                        ? [...(prev[projectId]?.[taxonomySlug] || []), ...newTerms]
                        : newTerms
                }
            }));

            setTaxonomyPagination(prev => ({
                ...prev,
                [projectId]: {
                    ...(prev[projectId] || {}),
                    [taxonomySlug]: {
                        page,
                        hasMore: response.hasMore || false,
                        total: response.total || 0
                    }
                }
            }));
        } catch (e: any) {
            console.error('[fetchTaxonomyTerms] Error:', e);

            // Manejar específicamente el error 404 (taxonomía no disponible en WordPress)
            if (e?.response?.status === 404) {
                const taxConfig = enabledTaxonomies[projectId]?.find((t: any) => t.slug === taxonomySlug);
                const taxLabel = taxConfig?.label || taxonomySlug;
                setToast({
                    message: `La taxonomía "${taxLabel}" no está disponible en tu WordPress. Verifica la configuración del proyecto.`,
                    type: 'error'
                });
            }

            if (!append) {
                setProjectTaxonomies(prev => ({
                    ...prev,
                    [projectId]: {
                        ...(prev[projectId] || {}),
                        [taxonomySlug]: []
                    }
                }));

                // Inicializar paginación vacía para evitar estados inconsistentes
                setTaxonomyPagination(prev => ({
                    ...prev,
                    [projectId]: {
                        ...(prev[projectId] || {}),
                        [taxonomySlug]: {
                            page: 1,
                            hasMore: false,
                            total: 0
                        }
                    }
                }));
            }
        } finally {
            setIsLoadingPages(false);
        }
    };

    // Cargar más términos al hacer scroll
    const loadMoreTaxonomyTerms = (projectId: string, taxonomySlug: string) => {
        const currentPagination = taxonomyPagination[projectId]?.[taxonomySlug];
        if (!currentPagination || !currentPagination.hasMore || isLoadingPages) return;

        const searchQuery = taxonomySearch[`${projectId}-${taxonomySlug}`] || '';
        fetchTaxonomyTerms(projectId, taxonomySlug, searchQuery, currentPagination.page + 1, true);
    };

    // Búsqueda en tiempo real con debounce
    const handleTaxonomySearch = (projectId: string, taxonomySlug: string, value: string) => {
        const key = `${projectId}-${taxonomySlug}`;
        console.log('[handleTaxonomySearch] Key:', key, 'Value:', value);
        setTaxonomySearch(prev => ({ ...prev, [key]: value }));

        // Limpiar timeout anterior
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        // Hacer búsqueda después de 500ms sin teclear
        searchTimeoutRef.current = setTimeout(() => {
            console.log('[handleTaxonomySearch] Ejecutando búsqueda:', { projectId, taxonomySlug, value });
            fetchTaxonomyTerms(projectId, taxonomySlug, value, 1, false);
        }, 500);
    };

    const addRow = (targetProjectId: string) => {
        const newRow: SchedulerRow = {
            id: `row-${Date.now()}`,
            projectId: targetProjectId,
            topic: '',
            isNewTopic: false, // Default to existing topic selection
            keyword: '',
            audience: '',
            stylePosts: [],
            externalUrls: [],
            includeStructure: false,
            linkedPages: [],
            linkedTaxonomyTerms: [],
            linkedUrls: [],
            predefinedStyle: '', // Default style empty
            includeIndex: '',
            faqDesplegable: '',
            status: 'pending',
            progress: 0,
            isExpanded: true,
            articleType: 'wikipedia',
            comparisonUrls: [],
            comparisonItems: [],
            comparisonSourceTab: 'wp-pages',
            mainSourceUrl: '',
            wordCount: 2000,
            ctaText: '',
            ctaUrl: '',
            isTitleStrict: true
        };
        setRows([...rows, newRow]);
        fetchProjectData(targetProjectId);
    };

    const removeRow = (id: string) => {
        setRows(prev => prev.filter(r => r.id !== id));
    };

    const updateRow = (id: string, updates: Partial<SchedulerRow>) => {
        setRows(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
    };

    const processRow = async (row: SchedulerRow) => {
        if (!row.projectId || !row.topic || !row.keyword || !row.includeIndex || !row.faqDesplegable) {
            updateRow(row.id, { status: 'error', errorMsg: 'Faltan campos obligatorios' });
            return;
        }

        // Helper to check cancellation
        const checkCancelled = () => {
            if (cancelledRef.current) {
                throw new Error('CANCELLED');
            }
        };

        try {
            // STEP 0: Style Analysis (if WP posts or external URLs selected)
            let styleAnalysis = '';

            // External URLs style analysis (SKIP for 'news' type which uses mainSourceUrl instead)
            if (row.predefinedStyle === 'external' && row.articleType !== 'news' && row.externalUrls && row.externalUrls.filter(u => u.trim()).length > 0) {
                updateRow(row.id, { status: 'briefing', progress: 5, errorMsg: undefined });
                const includeStructure = row.includeStructure || false;

                // Filter only valid URLs (must start with http:// or https://)
                const validUrls = row.externalUrls.filter(u => {
                    const trimmed = u.trim();
                    if (!trimmed) return false;
                    try {
                        new URL(trimmed);
                        return true;
                    } catch {
                        return false;
                    }
                });

                if (validUrls.length > 0) {
                    try {
                        const analysisResult = await webhookService.analyzeStyleFromExternalUrls(validUrls, undefined, includeStructure);
                        styleAnalysis = analysisResult.styleAnalysis || JSON.stringify(analysisResult);
                        updateRow(row.id, { styleAnalysis });
                    } catch (error) {
                        // Error in external style analysis
                    }
                }
            }

            // WordPress posts style analysis
            if (row.predefinedStyle === 'wordpress' && row.stylePosts.length > 0) {
                updateRow(row.id, { status: 'briefing', progress: 5, errorMsg: undefined });

                const selectedPosts = projectWPPosts[row.projectId]?.filter(p => row.stylePosts.includes(String(p.id)));

                if (selectedPosts && selectedPosts.length > 0) {
                    // API expects { posts: [...] }. analyzeStyle takes array.
                    // Para tipos 'standard' y 'news' usa OpenAI GPT-4.1-mini, otros usan n8n
                    // projectId: necesario para extraer contenido completo de WordPress
                    const analysisResult = await webhookService.analyzeStyle(selectedPosts, undefined, row.articleType, row.projectId);
                    styleAnalysis = analysisResult.styleAnalysis || analysisResult.analysis || JSON.stringify(analysisResult);
                    updateRow(row.id, { styleAnalysis });
                }
            }

            // Check cancellation before briefing
            checkCancelled();

            // STEP 1: Briefing
            updateRow(row.id, { status: 'briefing', progress: 10, errorMsg: undefined });

            const topicName = row.isNewTopic ? row.topic :
                (projectTopics[row.projectId]?.find(t => t.id === row.topic)?.url || row.topic);

            // Construct Raw Text for Briefing
            const rawText = `
Tema: ${topicName}
Palabra clave principal: ${row.keyword}
Audiencia: ${row.audience || 'General'}
Tono: Profesional
Requisitos: Estilo basado en posts de WordPress seleccionados.
            `.trim();

            // Generate Brief
            const brief = await webhookService.generateBrief(rawText, row.keyword, undefined, undefined, row.articleType);

            // Check cancellation before research
            checkCancelled();

            // STEP 2: Research (One by one loop)
            updateRow(row.id, { status: 'researching', progress: 20, generatedBrief: brief });

            // Extract subtopics
            const subtopicsToResearch: string[] = brief.outline
                ? brief.outline.map(section => section.heading)
                : [topicName];

            const researchResults: any[] = [];

            // Loop for sequential research
            for (let i = 0; i < subtopicsToResearch.length; i++) {
                // Check cancellation before each subtopic
                checkCancelled();

                const subtopic = subtopicsToResearch[i];
                const progressPerItem = 40 / subtopicsToResearch.length; // Allocate 40% of standard bar to research
                const currentProgress = 20 + (i * progressPerItem);

                updateRow(row.id, {
                    status: 'researching',
                    progress: Math.round(currentProgress)
                });

                try {
                    const result = await webhookService.conductResearch([subtopic], topicName, undefined, row.articleType);
                    if (result && result.length > 0) {
                        researchResults.push(result[0]);
                    }
                } catch (err: any) {
                    // Re-throw cancellation errors
                    if (err.message === 'CANCELLED') throw err;
                    // Continue even if one fails
                }
            }

            updateRow(row.id, { researchResults });

            // Check cancellation before generation
            checkCancelled();

            // STEP 2.5: Extract and enrich comparison items for comparative articles
            let enrichedComparisonItems: any[] = [];
            if (row.articleType === 'comparative' && (row.comparisonItems || []).length >= 2) {
                updateRow(row.id, { status: 'generating', progress: 65 });

                try {
                    // Get project credentials for WordPress items
                    const project = projects.find(p => p.id === row.projectId) as any;
                    const wpCredentials = project?.wp_url && project?.wp_user && project?.wp_app_password
                        ? { url: project.wp_url, user: project.wp_user, appPassword: project.wp_app_password }
                        : (project?.wordpress_url ? { url: project.wordpress_url, user: '', appPassword: '' } : null);

                    const topicName = row.isNewTopic ? row.topic :
                        (projectTopics[row.projectId]?.find(t => t.id === row.topic)?.url || row.topic);

                    const extractionResult = await webhookService.extractComparisonItems(
                        row.comparisonItems || [],
                        topicName,
                        wpCredentials
                    );

                    if (extractionResult.success && extractionResult.items) {
                        enrichedComparisonItems = extractionResult.items;
                    }
                } catch (error) {
                    // Continue without enriched data - will use URLs only
                }
            }

            // STEP 3: Generation
            updateRow(row.id, { status: 'generating', progress: 70 });

            // Construct additional instructions/context from style analysis
            // If we have strict style analysis, we pass it.
            // Also pass the "styleContext" manually as fallback or context?
            // generateArticle signature: (brief, research, instructions, projectId, keywords, styleAnalysis)

            console.log('[ContentScheduler] About to generate article with taxonomy terms:', row.linkedTaxonomyTerms);

            const generatedArticle = await webhookService.generateArticle(
                brief,
                researchResults,
                `Tono deseado: ${row.predefinedStyle || 'Profesional'}.`, // Additional instructions with Tone
                row.projectId,
                [row.keyword],
                styleAnalysis, // Pass the analysis result here!
                row.linkedPages, // Pass linkedPages as 7th argument
                row.includeIndex || 'no', // Pass includeIndex string
                row.faqDesplegable || 'no', // Pass faqDesplegable string
                row.articleType || 'wikipedia', // Tipo de artículo
                row.comparisonUrls || [], // URLs para comparativa
                row.mainSourceUrl || '', // URL fuente principal para noticias
                row.wordCount || 2000, // Cantidad de palabras objetivo
                enrichedComparisonItems.length > 0 ? enrichedComparisonItems : undefined, // Enriched comparison items
                row.linkedUrls || [], // URLs externas con info scrapeada para enlaces internos
                row.ctaText, // CTA text
                row.ctaUrl, // CTA URL
                row.isTitleStrict, // Title strict mode
                row.linkedTaxonomyTerms || [] // Términos de taxonomía seleccionados
            );

            // STEP 4: Save & Adapt for Viewer
            const saveResponse = await projectService.saveArticle(row.projectId, generatedArticle);

            // ArticleViewer expects a DB-like object with { article_data: ... }
            // The saveResponse.article might have it, or we construct it.
            const savedArticle = saveResponse?.article || {};

            // Construct the viewable object ensuring 'article_data' exists
            const viewableArticle = {
                id: savedArticle.id || `temp-${Date.now()}`,
                status: 'completed',
                created_at: savedArticle.created_at || new Date().toISOString(),
                session_id: savedArticle.session_id || row.id,
                // Prioritize saved article_data, fallback to wrapping the generated content
                article_data: savedArticle.article_data || generatedArticle
            };

            updateRow(row.id, {
                status: 'completed',
                progress: 100,
                resultArticle: viewableArticle
            });

        } catch (error: any) {
            // Handle cancellation specially
            if (error.message === 'CANCELLED') {
                updateRow(row.id, { status: 'pending', progress: 0, errorMsg: 'Cancelado por el usuario' });
                return;
            }
            updateRow(row.id, { status: 'error', errorMsg: error.message || 'Error desconocido' });
        }
    };

    const handleStartAll = async () => {
        // Reset cancellation state
        cancelledRef.current = false;
        setIsCancelled(false);
        setIsProcessing(true);

        // Process strictly sequentially to avoid overwhelming endpoints
        for (const row of rows) {
            // Check if cancelled between rows
            if (cancelledRef.current) {
                break;
            }
            if (row.status === 'pending' || row.status === 'error') {
                await processRow(row);
            }
        }

        setIsProcessing(false);
        setIsCancelled(false);
    };

    const handleCancelAll = useCallback(() => {
        cancelledRef.current = true;
        setIsCancelled(true);
        // Cancel any ongoing API requests
        apiCancelTokens.cancelResearch();
        apiCancelTokens.cancelArticleGeneration();
    }, []);

    const getStatusText = (status: SchedulerRow['status']) => {
        switch (status) {
            case 'pending': return 'Pendiente';
            case 'briefing': return 'Creando Brief...';
            case 'researching': return 'Investigando...';
            case 'generating': return 'Escribiendo...';
            case 'error': return 'Error';
        }
    };

    function getStylesDropdown(row: SchedulerRow) {
        if (activeDropdown?.id !== `wp-${row.id}`) return null;

        return (
            <>
                <div
                    className="fixed inset-0 z-[9998] bg-transparent"
                    onClick={() => setActiveDropdown(null)}
                ></div>
                <div
                    style={{
                        position: 'fixed',
                        top: activeDropdown.top,
                        left: activeDropdown.left,
                        width: activeDropdown.width,
                        zIndex: 9999
                    }}
                    className="bg-white rounded-xl shadow-xl border border-gray-200 max-h-80 overflow-y-auto animate-in fade-in zoom-in-95 duration-100 p-2"
                >
                    <div className="mb-2 px-2 py-1 flex justify-between items-center border-b border-gray-50">
                        <span className="text-[10px] font-bold text-gray-400 uppercase">Selecciona posts base</span>
                    </div>
                    {projectWPPosts[row.projectId]?.length > 0 ? projectWPPosts[row.projectId].map(post => {
                        const isSelected = row.stylePosts.includes(String(post.id));
                        return (

                            <div key={post.id} className={`flex items-center gap-2 p-2 rounded hover:bg-gray-50 group/item ${isSelected ? 'bg-gray-100' : ''}`}>
                                <label className="flex-1 flex items-start gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        className="mt-1 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                                        checked={isSelected}
                                        onChange={(e) => {
                                            const currentRaw = row.stylePosts;
                                            if (e.target.checked) {
                                                if (currentRaw.length >= 3) {
                                                    setToast({ message: '⚠️ Máximo 3 posts de referencia permitidos', type: 'warning' });
                                                    return;
                                                }
                                                updateRow(row.id, { stylePosts: [...currentRaw, String(post.id)] });
                                            } else {
                                                updateRow(row.id, { stylePosts: currentRaw.filter(id => id !== String(post.id)) });
                                            }
                                        }}
                                    />
                                    <span className="text-xs text-gray-700 line-clamp-2">{post.title}</span>
                                </label>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(post.link, '_blank');
                                    }}
                                    className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover/item:opacity-100 transition-opacity"
                                    title="Ver post original"
                                >
                                    <ExternalLink className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )

                    }) : <p className="text-xs text-gray-400 p-2">No hay posts disponibles para este proyecto.</p>}
                </div>
            </>
        );
    }

    // Función para extraer info de una URL y añadirla a linkedUrls
    const handleExtractUrl = async (row: SchedulerRow) => {
        if (!urlInput.trim()) return;

        // Validar URL
        try {
            new URL(urlInput);
        } catch {
            setToast({ message: 'URL inválida', type: 'error' });
            return;
        }

        // Verificar si ya existe
        if (row.linkedUrls?.some(u => u.url === urlInput)) {
            setToast({ message: 'Esta URL ya está añadida', type: 'warning' });
            return;
        }

        setIsExtractingUrl(true);
        try {
            const linkInfo = await extractLinkInfo(urlInput);
            updateRow(row.id, {
                linkedUrls: [...(row.linkedUrls || []), linkInfo]
            });
            setUrlInput('');
            setToast({ message: `Enlace añadido: ${linkInfo.title}`, type: 'success' });
        } catch (error: any) {
            setToast({ message: error.message || 'Error al extraer información', type: 'error' });
        } finally {
            setIsExtractingUrl(false);
        }
    };

    // Función para eliminar una URL de linkedUrls
    const handleRemoveLinkedUrl = (row: SchedulerRow, urlToRemove: string) => {
        updateRow(row.id, {
            linkedUrls: (row.linkedUrls || []).filter(u => u.url !== urlToRemove)
        });
    };

    function getPagesDropdown(row: SchedulerRow) {
        if (activeDropdown?.id !== `pages-${row.id}`) return null;

        const totalLinks = (row.linkedPages?.length || 0) + (row.linkedUrls?.length || 0) + (row.linkedTaxonomyTerms?.length || 0);

        return (
            <>
                <div
                    className="fixed inset-0 z-[9998] bg-transparent"
                    onClick={() => setActiveDropdown(null)}
                ></div>
                <div
                    style={{
                        position: 'fixed',
                        top: activeDropdown.top,
                        left: activeDropdown.left,
                        width: Math.max(activeDropdown.width, 380),
                        zIndex: 9999
                    }}
                    className="bg-white rounded-xl shadow-xl border border-gray-200 max-h-[420px] overflow-hidden animate-in fade-in zoom-in-95 duration-100"
                >
                    {/* Tabs Header - Dynamic based on enabled taxonomies */}
                    <div className="sticky top-0 bg-white z-10 border-b border-gray-100">
                        <div className="flex overflow-x-auto">
                            {/* Páginas WP Tab (always shown) */}
                            <button
                                onClick={() => setLinksTab('page')}
                                className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors whitespace-nowrap ${
                                    linksTab === 'page'
                                        ? 'text-gray-900 border-b-2 border-gray-900 bg-gray-50'
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                <span className="flex items-center justify-center gap-1.5">
                                    <FileStack className="w-3.5 h-3.5" />
                                    Páginas
                                    {(row.linkedPages?.length || 0) > 0 && (
                                        <span className="bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded-full text-[10px]">
                                            {row.linkedPages?.length}
                                        </span>
                                    )}
                                </span>
                            </button>

                            {/* Dynamic Taxonomy Tabs */}
                            {(() => {
                                console.log('[ContentScheduler RENDER] Taxonomies for project', row.projectId, ':', enabledTaxonomies[row.projectId]);
                                return (enabledTaxonomies[row.projectId] || []).map((tax: any) => (
                                    <button
                                        key={tax.slug}
                                        onClick={() => {
                                            setLinksTab(tax.slug);
                                            fetchTaxonomyTerms(row.projectId, tax.slug);
                                        }}
                                    className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors whitespace-nowrap ${
                                        linksTab === tax.slug
                                            ? 'text-gray-900 border-b-2 border-gray-900 bg-gray-50'
                                            : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                    }`}
                                >
                                    <span className="flex items-center justify-center gap-1.5">
                                        {tax.label}
                                    </span>
                                </button>
                                ));
                            })()}

                            {/* URLs Externas Tab (always shown) */}
                            <button
                                onClick={() => setLinksTab('urls')}
                                className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors whitespace-nowrap ${
                                    linksTab === 'urls'
                                        ? 'text-emerald-600 border-b-2 border-emerald-600 bg-emerald-50/50'
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                                }`}
                            >
                                <span className="flex items-center justify-center gap-1.5">
                                    <Link className="w-3.5 h-3.5" />
                                    URLs
                                    {(row.linkedUrls?.length || 0) > 0 && (
                                        <span className="bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full text-[10px]">
                                            {row.linkedUrls?.length}
                                        </span>
                                    )}
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* Tab Content */}
                    <div className="overflow-y-auto max-h-[340px]">
                        {linksTab === 'page' ? (
                            <>
                                {/* WP Pages Search */}
                                <div className="sticky top-0 bg-white z-10 p-2 border-b border-gray-50">
                                    <div className="flex gap-1">
                                        <div className="relative flex-1">
                                            {isLoadingPages ? (
                                                <Loader2 className="absolute left-2 top-2.5 w-3.5 h-3.5 text-gray-600 animate-spin" />
                                            ) : (
                                                <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-gray-400" />
                                            )}
                                            <input
                                                type="text"
                                                placeholder="Buscar página WP..."
                                                className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100 placeholder-gray-400"
                                                value={pagesSearch}
                                                onChange={(e) => setPagesSearch(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        fetchProjectData(row.projectId, pagesSearch);
                                                    }
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        <Button
                                            size="xs"
                                            variant="secondary"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                fetchProjectData(row.projectId, pagesSearch);
                                            }}
                                            disabled={isLoadingPages}
                                            className="h-auto py-1.5 px-3"
                                        >
                                            Buscar
                                        </Button>
                                    </div>
                                </div>
                                <div className="p-2">
                                    {(projectWPPages[row.projectId] || []).length > 0 ? (
                                        [...(projectWPPages[row.projectId] || [])]
                                            .sort((a, b) => {
                                                const isA = row.linkedPages?.includes(String(a.id)) ? 1 : 0;
                                                const isB = row.linkedPages?.includes(String(b.id)) ? 1 : 0;
                                                return isB - isA;
                                            })
                                            .map(page => {
                                                const isSelected = row.linkedPages?.includes(String(page.id));
                                                return (
                                                    <div key={page.id} className={`flex items-center gap-2 p-2 rounded hover:bg-gray-50 group/item ${isSelected ? 'bg-gray-100' : ''}`}>
                                                        <label className="flex-1 flex items-start gap-2 cursor-pointer">
                                                            <input
                                                                type="checkbox"
                                                                className="mt-1 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                                                                checked={isSelected}
                                                                onChange={(e) => {
                                                                    const currentPages = row.linkedPages || [];
                                                                    if (e.target.checked) {
                                                                        updateRow(row.id, { linkedPages: [...currentPages, String(page.id)] });
                                                                    } else {
                                                                        updateRow(row.id, { linkedPages: currentPages.filter(id => id !== String(page.id)) });
                                                                    }
                                                                }}
                                                            />
                                                            <div className="flex flex-col gap-0.5 w-full">
                                                                <span className="text-xs text-gray-700 font-medium line-clamp-2 leading-tight">
                                                                    {page.title}
                                                                </span>
                                                                {page.excerpt && (
                                                                    <span
                                                                        className="text-[10px] text-gray-500 line-clamp-2 leading-tight"
                                                                        dangerouslySetInnerHTML={{ __html: page.excerpt }}
                                                                    />
                                                                )}
                                                            </div>
                                                        </label>
                                                        <button
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                window.open(page.link, '_blank');
                                                            }}
                                                            className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover/item:opacity-100 transition-opacity"
                                                            title="Ver página original"
                                                        >
                                                            <ExternalLink className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                )
                                            })
                                    ) : (
                                        <div className="p-4 text-center">
                                            {isLoadingPages ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    <Loader2 className="w-5 h-5 text-gray-600 animate-spin" />
                                                    <span className="text-xs text-gray-400">Cargando páginas...</span>
                                                </div>
                                            ) : (
                                                <p className="text-xs text-gray-400">No hay páginas disponibles.</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : linksTab === 'urls' ? (
                            <>
                                {/* URLs Externas Input */}
                                <div className="sticky top-0 bg-white z-10 p-3 border-b border-gray-50">
                                    <p className="text-[10px] text-gray-500 mb-2">
                                        Pega una URL y extraeremos su información para enlazarla naturalmente en el artículo.
                                    </p>
                                    <div className="flex gap-1">
                                        <input
                                            type="url"
                                            placeholder="https://ejemplo.com/pagina-a-enlazar"
                                            className="flex-1 px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 placeholder-gray-400"
                                            value={urlInput}
                                            onChange={(e) => setUrlInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleExtractUrl(row);
                                                }
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            disabled={isExtractingUrl}
                                        />
                                        <Button
                                            size="xs"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleExtractUrl(row);
                                            }}
                                            disabled={isExtractingUrl || !urlInput.trim()}
                                            className="h-auto py-2 px-3 bg-emerald-600 hover:bg-emerald-700 text-white"
                                        >
                                            {isExtractingUrl ? (
                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                            ) : (
                                                <Plus className="w-3.5 h-3.5" />
                                            )}
                                        </Button>
                                    </div>
                                </div>
                                <div className="p-2">
                                    {(row.linkedUrls || []).length > 0 ? (
                                        (row.linkedUrls || []).map((link, idx) => (
                                            <div key={idx} className="flex items-start gap-2 p-2 rounded bg-emerald-50/50 hover:bg-emerald-50 mb-2 group/item">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-xs font-medium text-gray-800 truncate">
                                                            {link.title}
                                                        </span>
                                                    </div>
                                                    <p className="text-[10px] text-gray-500 line-clamp-2 mt-0.5">
                                                        {link.description || 'Sin descripción'}
                                                    </p>
                                                    <a
                                                        href={link.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-[10px] text-emerald-600 hover:underline truncate block mt-1"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        {link.url}
                                                    </a>
                                                    {link.keywords && link.keywords.length > 0 && (
                                                        <div className="flex flex-wrap gap-1 mt-1.5">
                                                            {link.keywords.slice(0, 3).map((kw, i) => (
                                                                <span key={i} className="text-[9px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">
                                                                    {kw}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleRemoveLinkedUrl(row, link.url);
                                                    }}
                                                    className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                                    title="Eliminar enlace"
                                                >
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="p-4 text-center">
                                            <Link className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                                            <p className="text-xs text-gray-400">No hay URLs añadidas.</p>
                                            <p className="text-[10px] text-gray-400 mt-1">Pega una URL arriba para extraer su información.</p>
                                        </div>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Taxonomy Terms Content */}
                                <div className="sticky top-0 bg-white z-10 p-2 border-b border-gray-50">
                                    <div className="flex flex-col gap-2">
                                        <div className="relative flex-1">
                                            {isLoadingPages && (taxonomyPagination[row.projectId]?.[linksTab]?.page || 1) === 1 ? (
                                                <Loader2 className="absolute left-2 top-2.5 w-3.5 h-3.5 text-gray-600 animate-spin" />
                                            ) : (
                                                <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-gray-400" />
                                            )}
                                            <input
                                                type="text"
                                                placeholder={`Buscar ${enabledTaxonomies[row.projectId]?.find((t: any) => t.slug === linksTab)?.label || 'términos'} en WordPress...`}
                                                className="w-full pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-gray-400 focus:ring-2 focus:ring-gray-100 placeholder-gray-400"
                                                value={taxonomySearch[`${row.projectId}-${linksTab}`] || ''}
                                                onChange={(e) => handleTaxonomySearch(row.projectId, linksTab, e.target.value)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        </div>
                                        {taxonomyPagination[row.projectId]?.[linksTab]?.total > 0 && (
                                            <div className="text-[10px] text-gray-500">
                                                Mostrando {(projectTaxonomies[row.projectId]?.[linksTab] || []).length} de {taxonomyPagination[row.projectId][linksTab].total}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div
                                    className="p-2 max-h-64 overflow-y-auto"
                                    onScroll={(e) => {
                                        const target = e.currentTarget;
                                        const isNearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 50;
                                        if (isNearBottom) {
                                            loadMoreTaxonomyTerms(row.projectId, linksTab);
                                        }
                                    }}
                                >
                                    {(projectTaxonomies[row.projectId]?.[linksTab] || []).length > 0 ? (
                                        <>
                                            {(projectTaxonomies[row.projectId]?.[linksTab] || []).map((term: any) => {
                                            const isSelected = row.linkedTaxonomyTerms?.some(t => t.id === String(term.id) && t.taxonomySlug === linksTab);
                                            const taxConfig = enabledTaxonomies[row.projectId]?.find((t: any) => t.slug === linksTab);
                                            return (
                                                <div key={term.id} className={`flex items-center gap-2 p-2 rounded hover:bg-gray-50 group/item ${isSelected ? 'bg-gray-100' : ''}`}>
                                                    <label className="flex-1 flex items-start gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            className="mt-1 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                                const currentTerms = row.linkedTaxonomyTerms || [];
                                                                if (e.target.checked) {
                                                                    const newTerm = {
                                                                        id: String(term.id),
                                                                        taxonomySlug: linksTab,
                                                                        restBase: taxConfig?.rest_base
                                                                    };
                                                                    console.log('[ContentScheduler] Adding taxonomy term:', newTerm);
                                                                    updateRow(row.id, {
                                                                        linkedTaxonomyTerms: [...currentTerms, newTerm]
                                                                    });
                                                                } else {
                                                                    console.log('[ContentScheduler] Removing taxonomy term:', { id: String(term.id), taxonomySlug: linksTab });
                                                                    updateRow(row.id, {
                                                                        linkedTaxonomyTerms: currentTerms.filter(t => !(t.id === String(term.id) && t.taxonomySlug === linksTab))
                                                                    });
                                                                }
                                                            }}
                                                        />
                                                        <div className="flex flex-col gap-0.5 w-full">
                                                            <span className="text-xs text-gray-700 font-medium line-clamp-2 leading-tight">
                                                                {term.title}
                                                            </span>
                                                            {term.excerpt && (
                                                                <span className="text-[10px] text-gray-500 line-clamp-2 leading-tight">
                                                                    {term.excerpt}
                                                                </span>
                                                            )}
                                                            {term.count && (
                                                                <span className="text-[9px] text-gray-400">
                                                                    {term.count} posts
                                                                </span>
                                                            )}
                                                        </div>
                                                    </label>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            window.open(term.link, '_blank');
                                                        }}
                                                        className="p-1 text-gray-400 hover:text-blue-600 opacity-0 group-hover/item:opacity-100 transition-opacity"
                                                        title="Ver en sitio"
                                                    >
                                                        <ExternalLink className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                        {/* Indicador de cargando más */}
                                        {isLoadingPages && (taxonomyPagination[row.projectId]?.[linksTab]?.page || 1) > 1 && (
                                            <div className="p-3 text-center">
                                                <Loader2 className="w-4 h-4 text-gray-400 animate-spin mx-auto" />
                                                <p className="text-[10px] text-gray-400 mt-1">Cargando más...</p>
                                            </div>
                                        )}
                                        {/* Botón para cargar más */}
                                        {!isLoadingPages && taxonomyPagination[row.projectId]?.[linksTab]?.hasMore && (
                                            <div className="p-2 text-center">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        loadMoreTaxonomyTerms(row.projectId, linksTab);
                                                    }}
                                                    className="text-xs text-gray-600 hover:text-gray-900 underline"
                                                >
                                                    Cargar más ({taxonomyPagination[row.projectId][linksTab].total - (projectTaxonomies[row.projectId]?.[linksTab] || []).length} restantes)
                                                </button>
                                            </div>
                                        )}
                                    </>
                                    ) : (
                                        <div className="p-4 text-center">
                                            {isLoadingPages ? (
                                                <div className="flex flex-col items-center gap-2">
                                                    <Loader2 className="w-5 h-5 text-gray-600 animate-spin" />
                                                    <span className="text-xs text-gray-400">Cargando términos...</span>
                                                </div>
                                            ) : (
                                                <p className="text-xs text-gray-400">No hay términos disponibles.</p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    {/* Footer con total */}
                    {totalLinks > 0 && (
                        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-100 px-3 py-2">
                            <p className="text-[10px] text-gray-500 text-center">
                                Total: <span className="font-medium text-gray-700">{totalLinks}</span> enlaces seleccionados
                            </p>
                        </div>
                    )}
                </div>
            </>
        );
    }

    if (isInitializing) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] w-full bg-white rounded-xl border border-gray-200 p-8">
                <Loader2 className="w-8 h-8 text-gray-400 animate-spin mb-4" />
                <h3 className="text-base font-medium text-gray-900">Cargando...</h3>
                <p className="text-sm text-gray-500 mt-1">Preparando el planificador</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <Card className="overflow-visible">
                <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
                    <h2 className="text-sm font-semibold text-gray-900">Cola de Producción</h2>
                    <div className="flex gap-2 relative">
                        <Button
                            onClick={toggleAddMenu}
                            icon={<Plus className="w-4 h-4" />}
                            variant="outline"
                            className={addMenuOpen ? 'bg-gray-100' : ''}
                        >
                            Añadir Fila
                        </Button>

                        {/* Dropdown Menu for Projects */}
                        {addMenuOpen && (
                            <>
                                <div className="fixed inset-0 z-10" onClick={() => setAddMenuOpen(false)}></div>
                                <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 z-20 animate-in fade-in zoom-in-95 duration-100 overflow-hidden">
                                    <div className="px-3 py-2 border-b border-gray-100">
                                        <span className="text-xs font-medium text-gray-500">Seleccionar proyecto</span>
                                    </div>
                                    <div className="max-h-60 overflow-y-auto p-1">
                                        {projects.map(p => (
                                            <button
                                                key={p.id}
                                                onClick={() => {
                                                    addRow(p.id);
                                                    setAddMenuOpen(false);
                                                }}
                                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors flex items-center justify-between group"
                                            >
                                                <span className="truncate flex-1">{p.name}</span>
                                                <Plus className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        {isProcessing ? (
                            <Button
                                onClick={handleCancelAll}
                                icon={<Square className="w-4 h-4" />}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                {isCancelled ? 'Cancelando...' : 'Detener Proceso'}
                            </Button>
                        ) : (
                            <Button
                                onClick={handleStartAll}
                                icon={<Play className="w-4 h-4" />}
                                disabled={rows.length === 0}
                                className="bg-green-600 hover:bg-green-700 text-white"
                            >
                                Iniciar Generación
                            </Button>
                        )}
                    </div>
                </div>

                <div className="space-y-8 p-4">
                    {rows.length === 0 && (
                        <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300">
                            <p className="text-gray-500 mb-2">No hay contenidos programados.</p>
                            <Button onClick={(e) => toggleAddMenu(e)} icon={<Plus className="w-4 h-4" />} variant="outline">Empezar a programar</Button>
                        </div>
                    )}

                    {/* Group Rows by Project */}
                    {Object.entries(rows.reduce((acc, row) => {
                        const pid = row.projectId || 'unassigned';
                        if (!acc[pid]) acc[pid] = [];
                        acc[pid].push(row);
                        return acc;
                    }, {} as Record<string, SchedulerRow[]>)).map(([projectId, projectRows]) => {
                        const project = projects.find(p => p.id === projectId);
                        return (
                            <div key={projectId} className="space-y-3">
                                {/* Project Header */}
                                <div className="flex items-center gap-3 pb-3 border-b border-gray-100 mb-4">
                                    <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                                        <Briefcase className="w-4 h-4 text-gray-500" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-medium text-gray-900 text-sm truncate">
                                            {project ? project.name : 'Sin Proyecto'}
                                        </h3>
                                        <p className="text-xs text-gray-400 truncate">
                                            {project ? project.wordpress_url : 'Selecciona un proyecto'}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => addRow(projectId)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                                    >
                                        <Plus className="w-3 h-3" />
                                        <span className="hidden sm:inline">Añadir</span>
                                    </button>
                                </div>

                                {/* Cards Grid */}
                                <div className="space-y-3">
                                    {projectRows.map((row, index) => (
                                        <div key={row.id} className={`bg-white border border-gray-200 rounded-xl transition-all relative overflow-hidden ${!row.isExpanded ? '' : 'shadow-sm'}`}>
                                            {/* Left Stripe */}
                                            <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${row.status === 'completed' ? 'bg-emerald-500' : row.status === 'error' ? 'bg-red-400' : 'bg-gray-300'}`}></div>

                                            {!row.isExpanded ? (
                                                // COLLAPSED VIEW
                                                <div className="flex items-center justify-between p-4 pl-6 cursor-pointer" onClick={() => updateRow(row.id, { isExpanded: true })}>
                                                    <div className="flex items-center gap-4 flex-1">
                                                        <span className="text-gray-400 text-[10px] font-bold">#{index + 1}</span>
                                                        <div className="flex-1">
                                                            <h4 className="font-medium text-gray-700 text-sm">
                                                                {row.topic && row.topic !== 'custom_new_topic_trigger' ?
                                                                    (row.isNewTopic ? row.topic : (projectTopics[row.projectId]?.find(t => t.id === row.topic)?.url || row.topic || 'Tema Personalizado')) :
                                                                    (row.topic || 'Sin tema definido')}
                                                            </h4>
                                                            <div className="flex items-center gap-2 mt-1">
                                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${row.status === 'completed' ? 'bg-green-100 text-green-800 border-green-200' :
                                                                    row.status === 'error' ? 'bg-red-100 text-red-800 border-red-200' :
                                                                        'bg-blue-50 text-blue-700 border-blue-100'
                                                                    }`}>
                                                                    {getStatusText(row.status)}
                                                                </span>
                                                                {row.progress > 0 && row.status !== 'completed' && (
                                                                    <div className="flex items-center gap-2 flex-1 max-w-[100px]">
                                                                        <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
                                                                            <div className="h-full bg-blue-500 transition-all duration-500" style={{ width: `${row.progress}%` }}></div>
                                                                        </div>
                                                                        <span className="text-[10px] text-gray-400">{row.progress}%</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {row.status === 'completed' && (
                                                            <Button
                                                                size="sm"
                                                                className="bg-gray-900 text-white hover:bg-black text-[10px] h-7 px-3"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setViewingArticle(row.resultArticle!);
                                                                    setViewingProjectId(row.projectId);
                                                                }}
                                                            >
                                                                Ver
                                                            </Button>
                                                        )}
                                                        <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                                                            <Edit2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                // EXPANDED VIEW
                                                <div className="p-5 pl-6 space-y-5 animate-in slide-in-from-top-2 duration-200">
                                                    {/* Compact Header for Expanded View */}
                                                    <div className="flex justify-between items-start border-b border-gray-50 pb-3 -mt-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="bg-gray-100 text-gray-500 text-[10px] font-bold px-2 py-1 rounded">#{index + 1}</span>
                                                            <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Configuración de Tarea</span>
                                                        </div>
                                                        <button
                                                            onClick={() => updateRow(row.id, { isExpanded: false })}
                                                            className="text-gray-300 hover:text-gray-500"
                                                        >
                                                            <ChevronUp className="w-4 h-4" />
                                                        </button>
                                                    </div>

                                                    {/* Form Grid */}
                                                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                                                        {/* Row 0: Article Type Selection */}
                                                        <div className="md:col-span-12">
                                                            <label className="block text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-1.5">
                                                                Tipo de Articulo
                                                            </label>
                                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                                                {ARTICLE_TYPES.map((type) => (
                                                                    <button
                                                                        key={type.id}
                                                                        onClick={() => updateRow(row.id, {
                                                                            articleType: type.id,
                                                                            comparisonUrls: type.id !== 'comparative' ? [] : row.comparisonUrls,
                                                                            comparisonItems: type.id !== 'comparative' ? [] : row.comparisonItems,
                                                                            mainSourceUrl: type.id !== 'news' ? '' : row.mainSourceUrl
                                                                        })}
                                                                        disabled={row.status !== 'pending'}
                                                                        className={`
                                                                            p-3 rounded-xl border-2 text-left transition-all text-xs
                                                                            ${row.articleType === type.id
                                                                                ? 'border-gray-900 bg-gray-50 ring-1 ring-gray-300'
                                                                                : 'border-gray-200 bg-white hover:border-gray-300'
                                                                            }
                                                                            ${row.status !== 'pending' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                                                                        `}
                                                                    >
                                                                        <div className="font-semibold text-gray-800">{type.name}</div>
                                                                        <div className="text-[10px] text-gray-500 mt-0.5 line-clamp-1">{type.features[0]}</div>
                                                                    </button>
                                                                ))}
                                                            </div>

                                                            {/* Campos adicionales según tipo */}
                                                            {row.articleType === 'comparative' && (
                                                                <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-in fade-in">
                                                                    <div className="flex items-center gap-2 mb-3">
                                                                        <Link className="w-4 h-4 text-gray-600" />
                                                                        <label className="text-[10px] uppercase font-bold text-gray-600 tracking-wider">
                                                                            Productos/Servicios a comparar
                                                                        </label>
                                                                        <span className="text-[10px] text-gray-500">(mín. 2, máx. 5)</span>
                                                                    </div>

                                                                    {/* Lista de items seleccionados */}
                                                                    {(row.comparisonItems || []).length > 0 && (
                                                                        <div className="space-y-2 mb-3">
                                                                            {(row.comparisonItems || []).map((item, idx) => (
                                                                                <div
                                                                                    key={idx}
                                                                                    className="flex items-center gap-2 p-2 bg-white rounded-lg border border-gray-200"
                                                                                >
                                                                                    <span className="w-5 h-5 bg-gray-200 text-gray-700 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                                                                                        {idx + 1}
                                                                                    </span>
                                                                                    <div className="flex-1 min-w-0">
                                                                                        <div className="flex items-center gap-1.5">
                                                                                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                                                                                                item.source === 'wp-page' ? 'bg-blue-100 text-blue-700' :
                                                                                                item.source === 'wp-post' ? 'bg-green-100 text-green-700' :
                                                                                                'bg-gray-200 text-gray-600'
                                                                                            }`}>
                                                                                                {item.source === 'wp-page' ? 'Página' : item.source === 'wp-post' ? 'Post' : 'Externa'}
                                                                                            </span>
                                                                                            <span className="text-xs font-medium text-gray-900 truncate">
                                                                                                {item.title.replace(/<[^>]*>/g, '')}
                                                                                            </span>
                                                                                        </div>
                                                                                        <span className="text-[10px] text-gray-400 truncate block">{item.url}</span>
                                                                                    </div>
                                                                                    <button
                                                                                        onClick={() => {
                                                                                            const newItems = (row.comparisonItems || []).filter((_, i) => i !== idx);
                                                                                            const newUrls = (row.comparisonUrls || []).filter(u => u !== item.url);
                                                                                            updateRow(row.id, { comparisonItems: newItems, comparisonUrls: newUrls });
                                                                                        }}
                                                                                        className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                                                                                    >
                                                                                        <X className="w-3.5 h-3.5 text-gray-400 hover:text-red-500" />
                                                                                    </button>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}

                                                                    {/* Tabs de fuente */}
                                                                    {(row.comparisonItems || []).length < 5 && (
                                                                        <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                                                                            {/* Tab headers */}
                                                                            <div className="flex border-b border-gray-100 bg-gray-50/50">
                                                                                <button
                                                                                    onClick={() => updateRow(row.id, { comparisonSourceTab: 'wp-pages' })}
                                                                                    className={`flex-1 px-3 py-2 text-[10px] font-medium flex items-center justify-center gap-1 transition-colors ${
                                                                                        row.comparisonSourceTab === 'wp-pages'
                                                                                            ? 'bg-white text-gray-900 border-b-2 border-gray-900 -mb-px'
                                                                                            : 'text-gray-500 hover:bg-gray-100'
                                                                                    }`}
                                                                                >
                                                                                    <FileStack className="w-3 h-3" />
                                                                                    WP Páginas
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => updateRow(row.id, { comparisonSourceTab: 'wp-posts' })}
                                                                                    className={`flex-1 px-3 py-2 text-[10px] font-medium flex items-center justify-center gap-1 transition-colors ${
                                                                                        row.comparisonSourceTab === 'wp-posts'
                                                                                            ? 'bg-white text-gray-900 border-b-2 border-gray-900 -mb-px'
                                                                                            : 'text-gray-500 hover:bg-gray-100'
                                                                                    }`}
                                                                                >
                                                                                    <PenTool className="w-3 h-3" />
                                                                                    WP Blog
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => updateRow(row.id, { comparisonSourceTab: 'manual' })}
                                                                                    className={`flex-1 px-3 py-2 text-[10px] font-medium flex items-center justify-center gap-1 transition-colors ${
                                                                                        row.comparisonSourceTab === 'manual'
                                                                                            ? 'bg-white text-gray-900 border-b-2 border-gray-900 -mb-px'
                                                                                            : 'text-gray-500 hover:bg-gray-100'
                                                                                    }`}
                                                                                >
                                                                                    <ExternalLink className="w-3 h-3" />
                                                                                    URL Manual
                                                                                </button>
                                                                            </div>

                                                                            {/* Tab content */}
                                                                            <div className="p-3">
                                                                                {/* WordPress Pages/Posts */}
                                                                                {(row.comparisonSourceTab === 'wp-pages' || row.comparisonSourceTab === 'wp-posts') && (
                                                                                    <>
                                                                                        {!projectWPPages[row.projectId] && !projectWPPosts[row.projectId] ? (
                                                                                            <div className="text-center py-4 text-gray-400">
                                                                                                <FileStack className="w-6 h-6 mx-auto mb-1 opacity-50" />
                                                                                                <p className="text-[10px]">Cargando contenido de WordPress...</p>
                                                                                            </div>
                                                                                        ) : (
                                                                                            <div className="max-h-36 overflow-y-auto space-y-1">
                                                                                                {(row.comparisonSourceTab === 'wp-pages'
                                                                                                    ? (projectWPPages[row.projectId] || [])
                                                                                                    : (projectWPPosts[row.projectId] || [])
                                                                                                ).map((wpItem) => {
                                                                                                    const isSelected = (row.comparisonItems || []).some(i => i.url === wpItem.link);
                                                                                                    return (
                                                                                                        <button
                                                                                                            key={wpItem.id}
                                                                                                            onClick={() => {
                                                                                                                if (isSelected) return;
                                                                                                                const newItem: ComparisonItem = {
                                                                                                                    url: wpItem.link,
                                                                                                                    title: wpItem.title || 'Sin título',
                                                                                                                    source: row.comparisonSourceTab === 'wp-pages' ? 'wp-page' : 'wp-post',
                                                                                                                    wpId: wpItem.id,
                                                                                                                    excerpt: wpItem.excerpt || ''
                                                                                                                };
                                                                                                                updateRow(row.id, {
                                                                                                                    comparisonItems: [...(row.comparisonItems || []), newItem],
                                                                                                                    comparisonUrls: [...(row.comparisonUrls || []), wpItem.link]
                                                                                                                });
                                                                                                            }}
                                                                                                            disabled={isSelected}
                                                                                                            className={`w-full text-left p-2 rounded-lg flex items-center gap-2 transition-colors text-xs ${
                                                                                                                isSelected
                                                                                                                    ? 'bg-gray-100 border border-gray-200 cursor-default'
                                                                                                                    : 'hover:bg-gray-50 border border-transparent'
                                                                                                            }`}
                                                                                                        >
                                                                                                            {isSelected ? (
                                                                                                                <Check className="w-3 h-3 text-gray-900 flex-shrink-0" />
                                                                                                            ) : (
                                                                                                                <Plus className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                                                                                            )}
                                                                                                            <span className={`truncate ${isSelected ? 'text-gray-900 font-medium' : 'text-gray-700'}`}>
                                                                                                                {wpItem.title || 'Sin título'}
                                                                                                            </span>
                                                                                                        </button>
                                                                                                    );
                                                                                                })}
                                                                                                {(row.comparisonSourceTab === 'wp-pages'
                                                                                                    ? (projectWPPages[row.projectId] || [])
                                                                                                    : (projectWPPosts[row.projectId] || [])
                                                                                                ).length === 0 && (
                                                                                                    <p className="text-center py-3 text-[10px] text-gray-400">
                                                                                                        No se encontraron {row.comparisonSourceTab === 'wp-pages' ? 'páginas' : 'posts'}
                                                                                                    </p>
                                                                                                )}
                                                                                            </div>
                                                                                        )}
                                                                                    </>
                                                                                )}

                                                                                {/* URL Manual */}
                                                                                {row.comparisonSourceTab === 'manual' && (
                                                                                    <div className="space-y-2">
                                                                                        <p className="text-[10px] text-gray-500">
                                                                                            Pega URLs de productos externos
                                                                                        </p>
                                                                                        <div className="flex gap-2">
                                                                                            <input
                                                                                                type="text"
                                                                                                placeholder="https://ejemplo.com/producto"
                                                                                                className="flex-1 text-xs px-3 py-2 rounded-lg border border-gray-200 focus:border-gray-400 focus:ring-gray-400"
                                                                                                onKeyDown={(e) => {
                                                                                                    if (e.key === 'Enter') {
                                                                                                        const input = e.target as HTMLInputElement;
                                                                                                        const url = input.value.trim();
                                                                                                        if (url && !(row.comparisonItems || []).find(i => i.url === url)) {
                                                                                                            const newItem: ComparisonItem = {
                                                                                                                url,
                                                                                                                title: url,
                                                                                                                source: 'external'
                                                                                                            };
                                                                                                            updateRow(row.id, {
                                                                                                                comparisonItems: [...(row.comparisonItems || []), newItem],
                                                                                                                comparisonUrls: [...(row.comparisonUrls || []), url]
                                                                                                            });
                                                                                                            input.value = '';
                                                                                                        }
                                                                                                    }
                                                                                                }}
                                                                                            />
                                                                                            <Button
                                                                                                size="xs"
                                                                                                variant="outline"
                                                                                                onClick={(e) => {
                                                                                                    const input = (e.target as HTMLElement).parentElement?.querySelector('input') as HTMLInputElement;
                                                                                                    const url = input?.value.trim();
                                                                                                    if (url && !(row.comparisonItems || []).find(i => i.url === url)) {
                                                                                                        const newItem: ComparisonItem = {
                                                                                                            url,
                                                                                                            title: url,
                                                                                                            source: 'external'
                                                                                                        };
                                                                                                        updateRow(row.id, {
                                                                                                            comparisonItems: [...(row.comparisonItems || []), newItem],
                                                                                                            comparisonUrls: [...(row.comparisonUrls || []), url]
                                                                                                        });
                                                                                                        if (input) input.value = '';
                                                                                                    }
                                                                                                }}
                                                                                                icon={<Plus className="w-3 h-3" />}
                                                                                                className="text-gray-700 border-gray-300"
                                                                                            >
                                                                                                Añadir
                                                                                            </Button>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                    {/* Status messages */}
                                                                    {(row.comparisonItems || []).length < 2 && (
                                                                        <p className="text-[10px] text-amber-600 mt-2">
                                                                            Necesitas al menos 2 productos/servicios
                                                                        </p>
                                                                    )}
                                                                    {(row.comparisonItems || []).length >= 2 && (
                                                                        <p className="text-[10px] text-green-600 mt-2">
                                                                            ✓ Se extraerá información y se generará comparativa
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {row.articleType === 'news' && (
                                                                <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200 animate-in fade-in">
                                                                    <label className="text-[10px] uppercase font-bold text-amber-600 tracking-wider mb-2 block">
                                                                        URL del Artículo a Clonar
                                                                    </label>
                                                                    <p className="text-[10px] text-amber-700 mb-2">
                                                                        Se extraerá el contenido y se reescribirá en otras palabras, incluyendo FAQs automáticas.
                                                                    </p>
                                                                    <input
                                                                        type="text"
                                                                        value={row.mainSourceUrl || ''}
                                                                        onChange={(e) => updateRow(row.id, { mainSourceUrl: e.target.value })}
                                                                        className="w-full text-xs px-3 py-2 rounded-lg border border-amber-200 focus:border-amber-500 focus:ring-amber-500"
                                                                        placeholder="https://ejemplo.com/articulo-a-clonar"
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Row 1: Topic Selection (Full Width) */}
                                                        <div className="md:col-span-12">
                                                            <label
                                                                htmlFor={!row.projectId || row.isNewTopic ? `topic-${row.id}` : undefined}
                                                                className="block text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-1.5"
                                                            >
                                                                Tema / Título
                                                            </label>
                                                            {row.projectId && !row.isNewTopic ? (
                                                                <div className="flex gap-2 items-center">
                                                                    <div className="flex-1">
                                                                        <Select
                                                                            options={[
                                                                                { value: 'custom_new_topic_trigger', label: '✏️ Escribir tema personalizado...' },
                                                                                ...(projectTopics[row.projectId]?.map(t => ({ value: t.id, label: t.url })) || [])
                                                                            ]}
                                                                            value={row.topic}
                                                                            onChange={(val) => {
                                                                                if (val === 'custom_new_topic_trigger') {
                                                                                    updateRow(row.id, { isNewTopic: true, topic: '' });
                                                                                } else {
                                                                                    updateRow(row.id, { topic: String(val) });
                                                                                }
                                                                            }}
                                                                            placeholder="-- Seleccionar Tema Sugerido --"
                                                                            disabled={row.status !== 'pending'}
                                                                        />
                                                                    </div>
                                                                    {row.topic && row.topic !== 'custom_new_topic_trigger' && row.status === 'pending' && (
                                                                        <button
                                                                            onClick={() => {
                                                                                const currentLabel = projectTopics[row.projectId]?.find(t => t.id === row.topic)?.url || '';
                                                                                updateRow(row.id, { isNewTopic: true, topic: currentLabel });
                                                                            }}
                                                                            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all"
                                                                            title="Editar texto manualmente"
                                                                        >
                                                                            <Edit2 className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div className="flex gap-2 items-center">
                                                                    <input
                                                                        id={`topic-${row.id}`}
                                                                        name={`topic-${row.id}`}
                                                                        type="text"
                                                                        placeholder="Escribe el tema / título..."
                                                                        className="flex-1 block w-full rounded-xl border-gray-200 bg-gray-50 focus:bg-white focus:border-gray-400 focus:ring-gray-400 text-sm transition-all shadow-sm py-3 px-4"
                                                                        value={row.topic}
                                                                        onChange={(e) => updateRow(row.id, { topic: e.target.value })}
                                                                        disabled={row.status !== 'pending'}
                                                                        autoFocus
                                                                    />
                                                                    <button
                                                                        onClick={() => updateRow(row.id, { isNewTopic: false, topic: '' })}
                                                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                                                        title="Cancelar edición"
                                                                    >
                                                                        <Trash2 className="w-4 h-4" />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Checkbox: Título Estricto - Right below topic field */}
                                                        <div className="md:col-span-12">
                                                            <label className="flex items-start gap-2 px-1 cursor-pointer group">
                                                                <div className="flex items-center h-5 pt-0.5">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={row.isTitleStrict ?? true}
                                                                        onChange={(e) => updateRow(row.id, { isTitleStrict: e.target.checked })}
                                                                        disabled={row.status !== 'pending'}
                                                                        className="w-3.5 h-3.5 text-blue-600 bg-white border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                                                    />
                                                                </div>
                                                                <div className="flex-1">
                                                                    <span className="text-xs font-semibold text-gray-600 group-hover:text-blue-600 transition-colors">
                                                                        Usar como título estricto
                                                                    </span>
                                                                    <p className="text-[10px] text-gray-400 mt-0.5 leading-relaxed">
                                                                        {row.isTitleStrict ?? true
                                                                            ? 'El título se usará literalmente como está escrito'
                                                                            : 'El tema se usará como referencia y la IA generará el título final'
                                                                        }
                                                                    </p>
                                                                </div>
                                                            </label>
                                                        </div>

                                                        {/* Row 2: Keyword, Audience, Style, Pages */}
                                                        {/* Main Keyword */}
                                                        <div className="md:col-span-3 space-y-1.5">
                                                            <label htmlFor={`keyword-${row.id}`} className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                                                                <svg className="w-3 h-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" /></svg>
                                                                Palabra Clave
                                                            </label>
                                                            <input
                                                                id={`keyword-${row.id}`}
                                                                name={`keyword-${row.id}`}
                                                                type="text"
                                                                placeholder="Ej: diseño web..."
                                                                className="block w-full rounded-xl border-gray-200 bg-gray-50 focus:bg-white focus:border-gray-400 focus:ring-gray-400 text-sm transition-all shadow-sm py-3 px-4"
                                                                value={row.keyword}
                                                                onChange={(e) => updateRow(row.id, { keyword: e.target.value })}
                                                                disabled={row.status !== 'pending'}
                                                            />
                                                        </div>

                                                        {/* Audience */}
                                                        <div className="md:col-span-3 space-y-1.5">
                                                            <label htmlFor={`audience-${row.id}`} className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                                                                <svg className="w-3 h-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                                                                Audiencia
                                                            </label>
                                                            <input
                                                                id={`audience-${row.id}`}
                                                                name={`audience-${row.id}`}
                                                                type="text"
                                                                placeholder="Ej: Emprendedores..."
                                                                className="block w-full rounded-xl border-gray-200 bg-gray-50 focus:bg-white focus:border-gray-400 focus:ring-gray-400 text-sm transition-all shadow-sm py-3 px-4"
                                                                value={row.audience}
                                                                onChange={(e) => updateRow(row.id, { audience: e.target.value })}
                                                                disabled={row.status !== 'pending'}
                                                            />
                                                        </div>

                                                        {/* Styles */}
                                                        <div className="md:col-span-3 space-y-1.5">
                                                            <label className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                                                                <svg className="w-3 h-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" /></svg>
                                                                Estilo <span className="text-red-500 ml-0.5">*</span>
                                                            </label>
                                                            <div className="flex flex-col gap-2">
                                                                <Select
                                                                    options={PREDEFINED_STYLES}
                                                                    value={row.predefinedStyle || ''}
                                                                    onChange={(val) => {
                                                                        const newVal = String(val);
                                                                        if (newVal === 'wordpress') {
                                                                            updateRow(row.id, { predefinedStyle: 'wordpress', externalUrls: [] });
                                                                        } else if (newVal === 'external') {
                                                                            updateRow(row.id, { predefinedStyle: 'external', stylePosts: [], externalUrls: [''], includeStructure: false });
                                                                        } else {
                                                                            updateRow(row.id, { predefinedStyle: newVal, stylePosts: [], externalUrls: [], includeStructure: false });
                                                                        }
                                                                    }}
                                                                    placeholder="Seleccionar Estilo..."
                                                                    disabled={row.status !== 'pending'}
                                                                />

                                                                {row.predefinedStyle === 'wordpress' && (
                                                                    <div className="relative animate-in fade-in slide-in-from-top-1">
                                                                        {row.projectId ? (
                                                                            <>
                                                                                <button
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        if (activeDropdown?.id === `wp-${row.id}`) {
                                                                                            setActiveDropdown(null);
                                                                                        } else {
                                                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                                                            setActiveDropdown({
                                                                                                id: `wp-${row.id}`,
                                                                                                top: rect.bottom + 8,
                                                                                                left: rect.left,
                                                                                                width: 320
                                                                                            });
                                                                                        }
                                                                                    }}
                                                                                    className={`
                                                                                        w-full flex items-center justify-between px-3 py-2 text-xs rounded-lg border transition-all
                                                                                        ${row.stylePosts.length > 0 ? 'bg-gray-100 border-gray-200 text-gray-700' : 'bg-white border-dashed border-gray-300 text-gray-500 hover:border-gray-400 hover:text-gray-700'}
                                                                                    `}
                                                                                >
                                                                                    <span className="flex items-center gap-2">
                                                                                        {row.stylePosts.length > 0 ? (
                                                                                            <>
                                                                                                <span className="flex h-1.5 w-1.5 rounded-full bg-gray-900"></span>
                                                                                                {row.stylePosts.length} posts
                                                                                            </>
                                                                                        ) : (
                                                                                            <>
                                                                                                <Plus className="w-3 h-3" /> Refs.
                                                                                            </>
                                                                                        )}
                                                                                    </span>
                                                                                    <svg className="w-3 h-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" /></svg>
                                                                                </button>
                                                                                {getStylesDropdown(row)}
                                                                            </>
                                                                        ) : (
                                                                            <p className="text-[10px] text-red-400 italic px-1">Selecciona proyecto</p>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {/* External URLs Mode */}
                                                                {row.predefinedStyle === 'external' && (
                                                                    <div className="space-y-2 animate-in fade-in slide-in-from-top-1">
                                                                        <p className="text-[10px] text-emerald-600 px-1">Añade URLs de artículos externos para analizar su estilo</p>

                                                                        {/* Checkbox para incluir estructura */}
                                                                        <label className="flex items-center gap-2 px-1 py-1.5 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={row.includeStructure || false}
                                                                                onChange={(e) => updateRow(row.id, { includeStructure: e.target.checked })}
                                                                                disabled={row.status !== 'pending'}
                                                                                className="w-3.5 h-3.5 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                                                                            />
                                                                            <span className="text-[10px] font-medium text-amber-800">
                                                                                Incluir análisis de ESTRUCTURA (para replicar formato exacto)
                                                                            </span>
                                                                        </label>
                                                                        {(row.externalUrls || ['']).map((url, urlIndex) => (
                                                                            <div key={urlIndex} className="flex items-center gap-1">
                                                                                <input
                                                                                    type="url"
                                                                                    value={url}
                                                                                    onChange={(e) => {
                                                                                        const newUrls = [...(row.externalUrls || [''])];
                                                                                        newUrls[urlIndex] = e.target.value;
                                                                                        updateRow(row.id, { externalUrls: newUrls });
                                                                                    }}
                                                                                    placeholder="https://ejemplo.com/articulo"
                                                                                    disabled={row.status !== 'pending'}
                                                                                    className="flex-1 px-2 py-1.5 text-xs rounded-lg border border-gray-200 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/20 transition-all"
                                                                                />
                                                                                {(row.externalUrls || []).length > 1 && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => {
                                                                                            const newUrls = (row.externalUrls || []).filter((_, i) => i !== urlIndex);
                                                                                            updateRow(row.id, { externalUrls: newUrls });
                                                                                        }}
                                                                                        disabled={row.status !== 'pending'}
                                                                                        className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                                                                    >
                                                                                        <Trash2 className="w-3 h-3" />
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                        {(row.externalUrls || []).length < 5 && row.status === 'pending' && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => updateRow(row.id, { externalUrls: [...(row.externalUrls || []), ''] })}
                                                                                className="flex items-center gap-1 text-[10px] text-emerald-600 hover:text-emerald-700 font-medium px-1 py-1 rounded hover:bg-emerald-50 transition-colors"
                                                                            >
                                                                                <Plus className="w-3 h-3" />
                                                                                Añadir URL
                                                                            </button>
                                                                        )}
                                                                        <p className="text-[9px] text-gray-400 px-1">{(row.externalUrls || []).filter(u => u.trim()).length} de 5 URLs</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Linked Pages (NEW) */}
                                                        <div className="md:col-span-3 space-y-1.5">
                                                            <label className="flex items-center gap-1.5 text-[10px] uppercase font-bold text-gray-400 tracking-wider">
                                                                <Link className="w-3 h-3 text-gray-500" />
                                                                Enlaces Internos
                                                            </label>
                                                            <div className="relative">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (activeDropdown?.id === `pages-${row.id}`) {
                                                                            setActiveDropdown(null);
                                                                        } else {
                                                                            setPagesSearch('');
                                                                            const rect = e.currentTarget.getBoundingClientRect();
                                                                            setActiveDropdown({
                                                                                id: `pages-${row.id}`,
                                                                                top: rect.bottom + 8,
                                                                                left: rect.left,
                                                                                width: 320
                                                                            });
                                                                        }
                                                                    }}
                                                                    className={`
                                                                        w-full flex items-center justify-between px-3 py-3 text-sm rounded-xl border transition-all
                                                                        ${((row.linkedPages || []).length + (row.linkedUrls || []).length + (row.linkedTaxonomyTerms || []).length) > 0 ? 'bg-gray-100 border-gray-300 text-gray-900 font-medium' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700'}
                                                                    `}
                                                                >
                                                                    <span className="flex items-center gap-2">
                                                                        {((row.linkedPages || []).length + (row.linkedUrls || []).length + (row.linkedTaxonomyTerms || []).length) > 0 ? (
                                                                            <>
                                                                                <Check className="w-3.5 h-3.5" />
                                                                                {(row.linkedPages || []).length > 0 && <span>{(row.linkedPages || []).length} pág.</span>}
                                                                                {(row.linkedPages || []).length > 0 && ((row.linkedUrls || []).length > 0 || (row.linkedTaxonomyTerms || []).length > 0) && <span>+</span>}
                                                                                {(row.linkedUrls || []).length > 0 && <span>{(row.linkedUrls || []).length} URL{(row.linkedUrls || []).length > 1 ? 's' : ''}</span>}
                                                                                {(row.linkedUrls || []).length > 0 && (row.linkedTaxonomyTerms || []).length > 0 && <span>+</span>}
                                                                                {(row.linkedTaxonomyTerms || []).length > 0 && <span>{(row.linkedTaxonomyTerms || []).length} term.</span>}
                                                                            </>
                                                                        ) : (
                                                                            'Seleccionar...'
                                                                        )}
                                                                    </span>
                                                                    <ChevronUp className={`w-4 h-4 transition-transform ${activeDropdown?.id === `pages-${row.id}` ? 'rotate-180' : 'rotate-0'}`} />
                                                                </button>
                                                                {getPagesDropdown(row)}
                                                            </div>
                                                        </div>

                                                        {/* Include Index */}
                                                        <div className="md:col-span-3">
                                                            <label className="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-1 block">
                                                                ¿Incluir Índice?
                                                            </label>
                                                            <Select
                                                                options={[
                                                                    { value: 'si', label: 'Sí, incluir índice' },
                                                                    { value: 'no', label: 'No' }
                                                                ]}
                                                                value={row.includeIndex || ''}
                                                                onChange={(val) => updateRow(row.id, { includeIndex: String(val) })}
                                                                disabled={row.status !== 'pending'}
                                                                placeholder="-- Seleccionar --"
                                                            />
                                                        </div>

                                                        {/* FAQ Collapsible */}
                                                        <div className="md:col-span-3">
                                                            <label className="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-1 block">
                                                                ¿FAQ Desplegable?
                                                            </label>
                                                            <Select
                                                                options={[
                                                                    { value: 'si', label: 'Sí, desplegable' },
                                                                    { value: 'no', label: 'No (Texto plano)' }
                                                                ]}
                                                                value={row.faqDesplegable || ''}
                                                                onChange={(val) => updateRow(row.id, { faqDesplegable: String(val) })}
                                                                disabled={row.status !== 'pending'}
                                                                placeholder="-- Seleccionar --"
                                                            />
                                                        </div>

                                                        {/* Word Count Slider */}
                                                        <div className="md:col-span-6">
                                                            <label className="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-1 block">
                                                                Cantidad de palabras: <span className="text-gray-800 font-extrabold text-sm">{row.wordCount || 2000}</span>
                                                            </label>
                                                            <div className="flex items-center gap-3">
                                                                <span className="text-xs text-gray-400">500</span>
                                                                <input
                                                                    type="range"
                                                                    min="500"
                                                                    max="5000"
                                                                    step="100"
                                                                    value={row.wordCount || 2000}
                                                                    onChange={(e) => updateRow(row.id, { wordCount: parseInt(e.target.value) })}
                                                                    disabled={row.status !== 'pending'}
                                                                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                />
                                                                <span className="text-xs text-gray-400">5000</span>
                                                            </div>
                                                            <div className="flex justify-between text-[9px] text-gray-400 mt-1 px-1">
                                                                <span>Corto</span>
                                                                <span>Medio</span>
                                                                <span>Largo</span>
                                                            </div>
                                                        </div>

                                                        {/* CTA Fields */}
                                                        <div className="md:col-span-3">
                                                            <label className="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-1 block">
                                                                CTA Texto (Opcional)
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={row.ctaText || ''}
                                                                onChange={(e) => updateRow(row.id, { ctaText: e.target.value })}
                                                                placeholder="Ej: Descubre más aquí"
                                                                disabled={row.status !== 'pending'}
                                                                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            />
                                                        </div>

                                                        <div className="md:col-span-3">
                                                            <label className="text-[10px] uppercase font-bold text-gray-400 tracking-wider mb-1 block">
                                                                CTA URL (Opcional)
                                                            </label>
                                                            <input
                                                                type="url"
                                                                value={row.ctaUrl || ''}
                                                                onChange={(e) => updateRow(row.id, { ctaUrl: e.target.value })}
                                                                placeholder="https://..."
                                                                disabled={row.status !== 'pending'}
                                                                className="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Expanded Footer with OK button */}
                                                    <div className="flex justify-between items-center pt-4 border-t border-gray-50">
                                                        <button
                                                            onClick={() => removeRow(row.id)}
                                                            className="text-gray-300 hover:text-red-500 transition-colors flex items-center gap-1.5 text-xs px-2 py-1 hover:bg-red-50 rounded-md"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" />
                                                            <span>Eliminar</span>
                                                        </button>

                                                        <Button
                                                            onClick={() => updateRow(row.id, { isExpanded: false })}
                                                            size="sm"
                                                            disabled={!row.topic || !row.keyword || !row.audience || !row.includeIndex || !row.faqDesplegable || !row.predefinedStyle || (row.predefinedStyle === 'wordpress' && row.stylePosts.length === 0) || (row.predefinedStyle === 'external' && (!row.externalUrls || row.externalUrls.filter(u => u.trim()).length === 0))}
                                                            className={`text-white text-xs px-6 shadow-md transition-all ${(!row.topic || !row.keyword || !row.audience || !row.includeIndex || !row.faqDesplegable || !row.predefinedStyle || (row.predefinedStyle === 'wordpress' && row.stylePosts.length === 0) || (row.predefinedStyle === 'external' && (!row.externalUrls || row.externalUrls.filter(u => u.trim()).length === 0))) ? 'bg-gray-300 cursor-not-allowed' : 'bg-gray-900 hover:bg-black hover:shadow-lg'}`}
                                                            icon={<Check className="w-3.5 h-3.5" />}
                                                        >
                                                            OK / Listo
                                                        </Button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Card>

            {/* Article Viewer Modal */}
            {viewingArticle && (
                <ArticleViewer
                    article={viewingArticle}
                    projectId={viewingProjectId}
                    onClose={() => {
                        setViewingArticle(null);
                        setViewingProjectId(undefined);
                    }}
                    onBack={() => {
                        setViewingArticle(null);
                        setViewingProjectId(undefined);
                    }}
                    onArticleUpdate={(updatedArticle) => {
                        // Update the specific row
                        const rowId = updatedArticle.session_id; // We stored row.id as session_id
                        if (rowId) {
                            setRows(prev => prev.map(r => {
                                if (r.id === rowId) {
                                    return {
                                        ...r,
                                        resultArticle: updatedArticle
                                    };
                                }
                                return r;
                            }));
                            // Update the currently viewing article to reflect changes immediately
                            setViewingArticle(updatedArticle);
                        }
                    }}
                />
            )}

            {/* Toast Notification */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-[10000] animate-in slide-in-from-bottom-5 fade-in duration-300 pointer-events-none">
                    <div className={`
                        px-4 py-3 rounded-lg shadow-xl text-sm font-medium backdrop-blur-sm border
                        ${toast.type === 'error' ? 'bg-red-50/90 text-red-800 border-red-200' :
                            toast.type === 'warning' ? 'bg-amber-50/90 text-amber-800 border-amber-200' :
                                'bg-gray-900/90 text-white border-gray-800'}
                    `}>
                        {toast.message}
                    </div>
                </div>
            )}
        </div>
    );

}
